import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuthInfo } from '@propelauth/react';
import useWorkspaceStore from '../../../../../store/workspace/workspaceStore';
import useAIResponseStream from '../../../../../components/agno/hooks/useAIResponseStream';
import APIRoutes from '../../../../../components/agno/vars';
import { constructEndpointUrl } from '../../../../../lib/constructEndpointUrl';
import { RunEvent } from '../../../../../types/os';
import useStore from '../../../../../components/agno/store';
import { mergeStreamingContent } from '../../../../../lib/mergeStreamingContent';
import { queryClient } from '../../../../../hooks/QueryHooks';

export interface SkillBuilderMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  isLoading?: boolean;
  toolCall?: {
    tool_call_id?: string;
    tool_name: string;
    result: any;
    args?: Record<string, unknown> | string | null;
    status?: 'running' | 'complete' | 'error';
    error?: string;
  };
}

export interface SkillBuilderDraftConfig {
  [key: string]: unknown;
  displayName?: string;
  name?: string;
  description?: string;
  kind?: 'capability' | 'playbook';
  instructionsContent?: string;
  keywords?: string[];
  priority?: number;
  structuredOutput?: StructuredOutputField[];
  status?: string;
  enabled?: boolean;
  visibility?: 'workspace' | 'private';
}

export interface StructuredOutputField {
  fieldName: string;
  fieldType: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
  required: boolean;
}

interface UseSkillBuilderStreamOptions {
  draftSkillConfig?: SkillBuilderDraftConfig | null;
}

interface SendSkillBuilderMessageOptions {
  draftOverride?: SkillBuilderDraftConfig | null;
}

export const INITIAL_SKILL_BUILDER_MESSAGE: SkillBuilderMessage = {
  id: 'initial',
  role: 'assistant',
  content: `Hi! I'm ORA and I'll help you create a new skill step by step.

**What would you like this skill to do?**

Describe the capability or playbook you have in mind (e.g. "Analyze account health and suggest actions", "Generate a weekly churn risk report").`,
};

function parseToolResult(result: any): any {
  if (typeof result !== 'string') return result;
  try {
    return JSON.parse(result);
  } catch {
    try {
      let fixed = result;
      fixed = fixed.replace(/UUID\('([^']+)'\)/g, '"$1"');
      fixed = fixed.replace(
        /datetime\.datetime\((\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+)[^)]*\)/g,
        (_: string, year: string, month: string, day: string, hour: string, minute: string, second: string, microsecond: string) =>
          `"${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}.${microsecond.padStart(6, '0').slice(0, 3)}Z"`
      );
      fixed = fixed
        .replace(/\bNone\b/g, 'null')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false');
      fixed = fixed.replace(/\\'/g, '___ESCAPED_QUOTE___');
      fixed = fixed.replace(/'/g, '"');
      fixed = fixed.replace(/___ESCAPED_QUOTE___/g, '\\"');
      return JSON.parse(fixed);
    } catch {
      return result;
    }
  }
}

const EMPTY_DRAFT_HASH = '__skill_builder_draft_empty__';

function createSkillBuilderSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `skill-builder-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function serializeDraft(draft: Record<string, unknown> | null | undefined): string {
  if (!draft || Object.keys(draft).length === 0) return EMPTY_DRAFT_HASH;
  try {
    return JSON.stringify(draft);
  } catch {
    return `skill-builder-draft-${Date.now()}`;
  }
}

export default function useSkillBuilderStream(options?: UseSkillBuilderStreamOptions) {
  const draftSkillConfig = options?.draftSkillConfig;
  const [messages, setMessages] = useState<SkillBuilderMessage[]>([INITIAL_SKILL_BUILDER_MESSAGE]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const pendingMessageRef = useRef<{
    text: string;
    options?: SendSkillBuilderMessageOptions;
  } | null>(null);

  const { streamResponse } = useAIResponseStream();
  const authInfo: any = useAuthInfo();
  const workspaceId =
    useWorkspaceStore((s) => s.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);
  const selectedEndpoint = useStore((s) => s.selectedEndpoint);
  const modelConfig = useStore((s) => s.modelConfig);
  const sessionStorageKey = workspaceId
    ? `he_skill_builder_session:${workspaceId}`
    : 'he_skill_builder_session';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(sessionStorageKey);
    const nextSessionId = saved || createSkillBuilderSessionId();
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    if (!saved) {
      localStorage.setItem(sessionStorageKey, nextSessionId);
    }
  }, [sessionStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionIdRef.current = sessionId;
    if (sessionId) {
      localStorage.setItem(sessionStorageKey, sessionId);
    }
  }, [sessionId, sessionStorageKey]);

  const buildRequestHeaders = useCallback(
    (activeSessionId: string | null) => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authInfo?.accessToken) headers.Authorization = `Bearer ${authInfo.accessToken}`;
      if (authInfo?.user?.userId) headers['X-User-Id'] = authInfo.user.userId;
      if (workspaceId) headers['X-Workspace-Id'] = String(workspaceId);
      if (activeSessionId) headers['X-Session-Id'] = activeSessionId;
      return headers;
    },
    [authInfo?.accessToken, authInfo?.user?.userId, workspaceId],
  );

  // Auto-send queued message once streaming finishes
  useEffect(() => {
    if (!isStreaming && pendingMessageRef.current) {
      const queued = pendingMessageRef.current;
      pendingMessageRef.current = null;
      setTimeout(() => sendMessage(queued.text, queued.options), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const sendMessage = useCallback(
    async (text: string, sendOptions?: SendSkillBuilderMessageOptions) => {
      if (!text.trim()) return;
      if (isStreaming) {
        pendingMessageRef.current = { text, options: sendOptions };
        return;
      }

      const userMsg: SkillBuilderMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: text,
      };

      const assistantMsg: SkillBuilderMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '',
        isLoading: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const endpointUrl = constructEndpointUrl(selectedEndpoint);
      const runUrl = APIRoutes.SkillsStream(endpointUrl);
      const activeSessionId = sessionIdRef.current || sessionId;
      const headers = buildRequestHeaders(activeSessionId);

      const draftForTurn =
        sendOptions?.draftOverride !== undefined
          ? sendOptions.draftOverride
          : draftSkillConfig ?? null;

      const requestBody: Record<string, unknown> = {
        message: text,
        agent_id: 'skill_builder',
        model_config: { model_key: modelConfig.model_key },
        state: {
          skill_builder_mode: true,
          ...(draftForTurn ? { skill_builder_draft: draftForTurn } : {}),
        },
      };

      try {
        await streamResponse({
          apiUrl: runUrl,
          headers,
          requestBody,
          onChunk: (chunk: any) => {
            if (chunk.session_id) {
              const incomingSessionId = String(chunk.session_id);
              sessionIdRef.current = incomingSessionId;
              setSessionId((prev) => (prev === incomingSessionId ? prev : incomingSessionId));
            }

            if (chunk.event === RunEvent.FirstToken) {
              setIsAssistantTyping(true);
              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant') {
                  last.isLoading = false;
                  last.content = '';
                }
                return msgs;
              });
            } else if (
              chunk.event === RunEvent.RunContent ||
              chunk.event === RunEvent.TeamRunContent
            ) {
              if (typeof chunk.content === 'string') {
                setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant') {
                    last.content = mergeStreamingContent(last.content || '', chunk.content as string);
                    last.isLoading = false;
                  } else {
                    const contentStr = chunk.content as string;
                    const alreadyExists = msgs.some(
                      (m) => m.role === 'assistant' && m.content && m.content.trim().includes(contentStr.trim()),
                    );
                    if (!alreadyExists) {
                      msgs.push({
                        id: `assistant-${Date.now()}-${Math.random()}`,
                        role: 'assistant',
                        content: contentStr,
                        isLoading: false,
                      });
                    }
                  }
                  return msgs;
                });
              }
            } else if (
              chunk.event === RunEvent.ToolCallStarted ||
              chunk.event === RunEvent.TeamToolCallStarted
            ) {
              setIsAssistantTyping(false);
              const rawTool = chunk.tool;
              const toolName = rawTool?.tool_name || rawTool?.name || rawTool?.toolName || 'tool';
              const toolCallId = rawTool?.tool_call_id || rawTool?.id;
              const runningToolMsg: SkillBuilderMessage = {
                id: `tool-running-${Date.now()}-${Math.random()}`,
                role: 'tool',
                content: '',
                toolCall: {
                  tool_call_id: toolCallId,
                  tool_name: String(toolName),
                  result: null,
                  args: rawTool?.args || rawTool?.tool_args || null,
                  status: 'running',
                },
              };

              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant' && !last.content) {
                  return [...msgs.slice(0, -1), runningToolMsg];
                }
                if (
                  last?.role === 'tool' &&
                  last.toolCall?.status === 'running' &&
                  last.toolCall?.tool_name === toolName
                ) {
                  return msgs;
                }
                return [...msgs, runningToolMsg];
              });
            } else if (
              chunk.event === RunEvent.ToolCallCompleted ||
              chunk.event === RunEvent.TeamToolCallCompleted
            ) {
              const rawTool = chunk.tool;
              if (!rawTool) return;

              const toolName = rawTool.tool_name || rawTool.name || rawTool.toolName || 'tool';
              const toolCallId = rawTool.tool_call_id || rawTool.id;
              const parsedResult = parseToolResult(rawTool.result);

              // If a skill was just created successfully, refresh the skills list
              if (
                parsedResult?.ui_step === 'skill_created' &&
                parsedResult?.success === true
              ) {
                queryClient.invalidateQueries({ queryKey: ['skills', String(workspaceId)] });
              }

              const toolMsg: SkillBuilderMessage = {
                id: `tool-${Date.now()}-${Math.random()}`,
                role: 'tool',
                content: '',
                toolCall: {
                  tool_call_id: toolCallId,
                  tool_name: toolName,
                  result: parsedResult,
                  args: rawTool.args || rawTool.tool_args || null,
                  status: 'complete',
                },
              };

              setMessages((prev) => {
                const msgs = [...prev];
                for (let index = msgs.length - 1; index >= 0; index -= 1) {
                  const message = msgs[index];
                  if (
                    message.role === 'tool' &&
                    message.toolCall?.status === 'running' &&
                    (
                      (toolCallId && message.toolCall?.tool_call_id === toolCallId) ||
                      message.toolCall?.tool_name === toolName
                    )
                  ) {
                    msgs[index] = toolMsg;
                    return msgs;
                  }
                }
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant' && !last.content && last.isLoading) {
                  return [...msgs.slice(0, -1), toolMsg];
                }
                return [...msgs, toolMsg];
              });
            } else if (chunk.event === RunEvent.RunCompletedEvent) {
              if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant') {
                    last.content = chunk.content as string;
                    last.isLoading = false;
                  } else {
                    const contentStr = chunk.content as string;
                    const alreadyExists = msgs.some(
                      (m) => m.role === 'assistant' && m.content && contentStr.startsWith(m.content.trim()),
                    );
                    if (!alreadyExists) {
                      msgs.push({
                        id: `assistant-${Date.now()}-${Math.random()}`,
                        role: 'assistant',
                        content: contentStr,
                        isLoading: false,
                      });
                    }
                  }
                  return msgs;
                });
              }
            } else if (
              chunk.event === RunEvent.RunCompleted ||
              chunk.event === RunEvent.TeamRunCompleted
            ) {
              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant') {
                  if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                    last.content = chunk.content as string;
                  }
                  last.isLoading = false;
                } else if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                  const contentStr = chunk.content as string;
                  const alreadyExists = msgs.some(
                    (m) => m.role === 'assistant' && m.content && contentStr.startsWith(m.content.trim()),
                  );
                  if (!alreadyExists) {
                    msgs.push({
                      id: `assistant-${Date.now()}-${Math.random()}`,
                      role: 'assistant',
                      content: contentStr,
                      isLoading: false,
                    });
                  }
                }
                return msgs;
              });
            }
          },
          onError: () => {
            setMessages((prev) => {
              const msgs = [...prev];
              const last = msgs[msgs.length - 1];
              if (last?.role === 'assistant') {
                last.content = 'Sorry, something went wrong. Please try again.';
                last.isLoading = false;
                return msgs;
              }
              return [
                ...msgs,
                {
                  id: `assistant-error-${Date.now()}`,
                  role: 'assistant',
                  content: 'Sorry, something went wrong. Please try again.',
                },
              ];
            });
          },
          onComplete: () => {
            setMessages((prev) => {
              const msgs = [...prev];
              const last = msgs[msgs.length - 1];
              if (last?.role === 'assistant') {
                last.isLoading = false;
              }
              return msgs;
            });
          },
        });
      } finally {
        setIsStreaming(false);
        setIsAssistantTyping(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildRequestHeaders, draftSkillConfig, isStreaming, modelConfig, selectedEndpoint, sessionId, streamResponse, workspaceId],
  );

  const resetChat = useCallback(() => {
    setMessages([INITIAL_SKILL_BUILDER_MESSAGE]);
    const nextSessionId = createSkillBuilderSessionId();
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    if (typeof window !== 'undefined') {
      localStorage.setItem(sessionStorageKey, nextSessionId);
    }
  }, [sessionStorageKey]);

  return { messages, isStreaming, isAssistantTyping, sendMessage, resetChat };
}
